// this needs to be loaded before React
import { hot } from 'react-hot-loader/root'

import React from 'react'
import {
  ConfirmDialog,
  QDSChrome,
  QuantumThemeWrapper,
} from '@sendoutcards/quantum-design-ui'

// @src imports
import Router from 'src/routes/Router'
import {
  DefaultError,
  HelpMenu,
  InvalidMagicLink,
  SideBar,
  SuspenseBoundary,
  Transition,
} from 'src/chrome'
import { BaseRoute } from 'src/routes/BaseRoute'
import { JoinModal } from 'src/user/components'
import {
  useActions,
  useCallback,
  useEffect,
  useFlag,
  useIntercomEffect,
  useLocalStorage,
  usePersistedState,
  usePersistedUserData,
  usePrefetchedQueryFutures,
  useProfitWellEffect,
  useQueryFutures,
  useQueryParams,
  useRedirectEffect,
  useSelector,
  useState,
  useUpdateSponsorEffect,
  useUserSnapEffect,
  useVertical,
} from 'src/hooks'

// tslint:disable-next-line: no-import-side-effect
import 'normalize.css'
// tslint:disable-next-line: no-import-side-effect
import 'src/app/styles/resets.scss'
import styles from 'src/chrome/styles/Chrome.module.scss'
import { FlexColumn, FlexRow } from 'src/styled'
import { InvalidMagicLinkRoute } from 'src/routes/InvalidMagicLinkRoute'
import { PricingRoute } from 'src/marketing/plans/routes/PricingRoute'
import { CatalogRoute } from 'src/catalog/routes/CatalogRoute'
import { GroupsRoute } from 'src/contacts/routes/GroupsRoute'
import { RelationshipManagerRoute } from 'src/contacts/routes/RelationshipManagerRoute'
import { ImportContactsRoute } from 'src/contacts/routes/ImportContactsRoute'
import { OrdersRoute } from 'src/orders/routes/OrdersRoute'
import { CardRoute } from 'src/card/routes/CardRoute'
import Card from 'src/card'
import { CampaignsRoute } from 'src/campaigns/routes/CampaignsRoute'
import { GiftStoreRoute } from 'src/gift_store/routes/GiftStoreRoute'
import { DashboardRoute } from 'src/dashboard/routes/DashboardRoute'
import CatalogPage from 'src/catalog/containers/CatalogPage'
import Notifications from 'src/dashboard/containers/Notifications/Notifications'
import GroupManager from 'src/contacts/containers/GroupManager'
import ContactManager from 'src/contacts/containers/ContactManager'
import ImportManager from 'src/import-contacts'
import Orders from 'src/orders/containers/Orders'
import { CampaignsRouter } from 'src/campaigns/components'
import GiftStore from 'src/gift_store/containers/GiftStore'
import Pricing from 'src/marketing/plans/containers/Pricing'
import { AccountRoute } from 'src/dashboard/routes/AccountRoute'
import { DraftedOrdersRoute } from 'src/dashboard/routes/DraftedOrdersRoute'
import { InvitesRoute } from 'src/dashboard/routes/InvitesRoute'
import { NotificationsRoute } from 'src/dashboard/routes/NotificationsRoute'
import DraftedOrders from 'src/dashboard/containers/DraftedOrders'
import { CardHistoryRoute } from 'src/dashboard/routes/CardHistoryRoute'
import { PaymentSettingsRoute } from 'src/dashboard/routes/PaymentSettingsRoute'
import PaymentSettings from 'src/dashboard/containers/PaymentSettings/PaymentSettings'
import CardHistoryManager from 'src/card_history/containers/CardHistoryManager'
import Account from 'src/dashboard/containers/Account'
import Invites from 'src/dashboard/containers/Invites/Invites'
import { DraftedCardsRoute } from 'src/dashboard/routes/DraftedCardsRoute'
import DraftedCards from 'src/drafts/containers/DraftedCards'
import Awards from 'src/awards/containers/Awards'
import { AwardsRoute } from 'src/dashboard/routes/AwardsRoute'
import { useNavSections } from './useNavSections'
import PhoneNumberVerification from 'src/onboarding/components/PhoneNumberVerification'
import Result, { success } from 'src/utils/Result'
import {
  GetAccount,
  getDetailedCountries,
  getMarketingBanners,
  getNotificationCount,
  UserType,
} from '../graphql'
import { perform } from './api'
import ProgressiveProfileActions from 'src/onboarding/components/ProgressiveProfileActions'

const App: React.FC = () => {
  const route = useSelector(state => state.route)
  const [shouldTryGetAccount, setShouldTryGetAccount] = useState(true)
  const queryParams = useQueryParams()
  const {
    account,
    controlData,
    phoneVerification,
    _persist: { rehydrated: isRehydrated },
  } = usePersistedUserData()

  const { isMobile } = useSelector(state => state.window)

  const actions = useActions()
  const storage = useLocalStorage()
  const vertical = useVertical()

  const [notificationCount] = useQueryFutures(getNotificationCount()).map(
    ({ value }) => value ?? 0,
  )

  const isNewNavigationEnabled = useFlag('New Navigation')
  const isSidebarEnabled = !BaseRoute.hideSidebar(route)

  const navSections = useNavSections(route, isNewNavigationEnabled)

  const [shouldPromptCookiesMessage, setShouldPromptCookiesMessage] = useState(
    !storage ? true : false,
  )

  const peContainerStyles: React.CSSProperties = !isSidebarEnabled
    ? { height: '100vh' }
    : {}

  const [isSideNavigationOpen, setIsSideNavigationOpen] = usePersistedState(
    'isSideNavigationOpen',
    true,
  )

  useIntercomEffect()

  const handleGetAccount = useCallback(async () => {
    try {
      const { account } = await perform(GetAccount())
      actions.loadedUser(Result(account))
    } catch {
      console.log('failed to get account information.')
    } finally {
      setShouldTryGetAccount(false)
    }
  }, [actions, setShouldTryGetAccount])

  useEffect(() => {
    if (shouldTryGetAccount) {
      handleGetAccount()
    }
  }, [shouldTryGetAccount, handleGetAccount])

  useUpdateSponsorEffect()
  useProfitWellEffect()
  useUserSnapEffect()
  useRedirectEffect()

  if (!isRehydrated) {
    return <div />
  }

  if (shouldTryGetAccount) {
    return (
      <Transition
        message={'Loading...'}
        css={{ width: '100%', height: '100%' }}
      />
    )
  }

  if (shouldPromptCookiesMessage) {
    return (
      <ConfirmDialog
        isOpen={true}
        title={`Welcome to ${vertical.name}`}
        description={`We have detected that your browser is blocking cookies. For a better experience, please, enable cookies.`}
        accept={{
          title: 'OK',
          onClick: () => setShouldPromptCookiesMessage(false),
        }}
      />
    )
  }

  if (
    !account ||
    (queryParams.isJoiningAffiliate && account && account.type !== UserType.GSA)
  ) {
    return controlData.token && !queryParams.isJoiningAffiliate ? (
      <Transition
        message={'Loading your account...'}
        css={{ width: '100%', height: '100%' }}
      />
    ) : controlData.shouldShowPhoneVerification ? (
      <>
        <PhoneNumberVerification
          loginPhoneNumber={phoneVerification.loginPhoneNumber}
          sponsorGenealogyIdOrSlug={queryParams.sponsor}
          onSuccess={({ verifiedAccount }) => {
            actions.loginDone(success(verifiedAccount))
          }}
        />
      </>
    ) : (
      <JoinModal
        initialAction={
          queryParams.join || (queryParams.isJoiningAffiliate && !account)
            ? 'join'
            : 'login'
        }
        isJoiningAffiliate={!!queryParams.isJoiningAffiliate}
      />
    )
  }

  if (account && controlData.shouldShowPhoneVerification) {
    return (
      <PhoneNumberVerification
        loginPhoneNumber={phoneVerification.loginPhoneNumber}
        sponsorGenealogyIdOrSlug={undefined}
        onSuccess={({ verifiedAccount }) => {
          actions.loginDone(success(verifiedAccount))
        }}
      />
    )
  }

  const path = route.subroute.path

  if (
    path !== DashboardRoute.path &&
    phoneVerification.unverifiedAccountsFound &&
    account
  ) {
    actions.openAccount()
  }

  if (!isNewNavigationEnabled) {
    return (
      <div
        style={peContainerStyles}
        className={`peContainer ${
          !isSidebarEnabled ? styles.hideSidebar : ''
        } ${!account && styles.noSidebar}`}
      >
        {isSidebarEnabled && <SideBar />}
        {!isMobile && <HelpMenu />}
        <Router />
        <ProgressiveProfileActions />
      </div>
    )
  }

  if (route.subroute.path === InvalidMagicLinkRoute.path) {
    return <InvalidMagicLink route={route.subroute} />
  }

  const isNewContactModalOpen =
    route.subroute.path === RelationshipManagerRoute.path
      ? route.subroute.isNewContactModalOpen
      : false
  const isBlankCardModalOpen = !!controlData.isBlankCardModalOpen

  return (
    <QDSChrome
      logo="socLogo"
      user={{
        firstName: account.firstName,
        lastName: account.lastName,
        profileImage: account.profileImageUrl ?? '',
        onClick: actions.openAccount,
      }}
      isExpanded={isSideNavigationOpen}
      setIsExpanded={() => setIsSideNavigationOpen(!isSideNavigationOpen)}
      isOnAccountRoute={
        path === DashboardRoute.path || path === PricingRoute.path
      }
      accountRouteOnClick={actions.openAccount}
      quickLinks={[
        {
          title: 'Build Your Own Card',
          icon: 'socLogo',
          onClick: () => {
            if (route.subroute.path === CatalogRoute.path) {
              actions.toggleIsBlankCardModalOpen()
            } else {
              actions.openCatalog()
              actions.toggleIsBlankCardModalOpen()
            }
          },
          isActive: isBlankCardModalOpen,
        },
        {
          title: 'Add Contact',
          icon: 'idCard',
          onClick: () => {
            if (route.subroute.path !== RelationshipManagerRoute.path) {
              actions.openRelationshipManager()
            }
            actions.toggleIsNewContactModalOpen()
          },
          isActive: isNewContactModalOpen,
        },
        {
          title: 'Manage Orders',
          icon: 'information',
          onClick: actions.openDraftedOrders,
          isActive:
            route.subroute.path === DashboardRoute.path &&
            route.subroute.subroute.path === DraftedOrdersRoute.path,
        },
      ]}
      navSections={navSections}
      accountSections={[
        [
          {
            badge: notificationCount >= 10 ? '10+' : `${notificationCount}`,
            onClick: actions.openNotifications,
            title: 'Notifications',
          },
        ],
        [
          {
            onClick: actions.openCardHistory,
            title: 'Card History',
          },
          {
            onClick: actions.openAwards,
            title: 'Awards',
          },
          {
            onClick: actions.openPaymentSettings,
            title: 'Payment Settings',
          },
          {
            onClick: actions.openPricing,
            title: 'Plans and Pricing',
          },
          {
            onClick: actions.openDraftedCards,
            title: 'Drafted Cards',
          },
          {
            onClick: actions.openDraftedOrders,
            title: 'Drafted Orders',
          },
        ],
        [
          {
            icon: 'tenDoor',
            onClick: actions.logout,
            title: 'Sign Out',
          },
        ],
      ]}
    >
      <FlexRow css={{ height: '100vh' }}>
        <HelpMenu />
        <FlexColumn css={{ flex: 1, height: '100%', overflow: 'auto' }}>
          {((): Exclude<JSX.Element, undefined> => {
            switch (route.subroute.path) {
              case AwardsRoute.path:
                return <Awards />
              case PricingRoute.path:
                return <Pricing route={route.subroute} />
              case CatalogRoute.path:
                return <CatalogPage route={route.subroute} />
              case GroupsRoute.path:
                return <GroupManager />
              case RelationshipManagerRoute.path:
                return <ContactManager />
              case ImportContactsRoute.path:
                return <ImportManager />
              case OrdersRoute.path:
                return <Orders route={route.subroute} />
              case CampaignsRoute.path:
                return <CampaignsRouter route={route.subroute} />
              case GiftStoreRoute.path:
                return <GiftStore route={route.subroute} />
              case CardRoute.path:
                return <Card cardId={route.subroute.cardId} />
              case DashboardRoute.path:
                switch (route.subroute.subroute.path) {
                  case AccountRoute.path:
                    return <Account />
                  case AwardsRoute.path:
                    return <Awards />
                  case DraftedOrdersRoute.path:
                    return <DraftedOrders />
                  case DraftedCardsRoute.path:
                    return <DraftedCards />
                  case InvitesRoute.path:
                    return <Invites />
                  case NotificationsRoute.path:
                    return <Notifications />
                  case CardHistoryRoute.path:
                    return (
                      <CardHistoryManager
                        route={route.subroute.subroute}
                        initialSection={route.subroute.subroute.section}
                        initialFilters={route.subroute.subroute.filters}
                      />
                    )
                  case PaymentSettingsRoute.path:
                    return <PaymentSettings />
                }
            }
          })()}
        </FlexColumn>
      </FlexRow>
    </QDSChrome>
  )
}

const AppWrapper = () => {
  usePrefetchedQueryFutures(getDetailedCountries(), getMarketingBanners())
  const { theme } = useVertical()
  const message = useSelector(
    state => state.graphql.blockingMutationTransitionMessage,
  )

  return (
    <QuantumThemeWrapper theme={theme}>
      <SuspenseBoundary
        unresolved={<Transition message="Loading..." />}
        failure={DefaultError}
      >
        <div>
          <App />
          {message && <Transition message={message} />}
        </div>
      </SuspenseBoundary>
    </QuantumThemeWrapper>
  )
}

export default hot(AppWrapper)
